import { createSignal, For, createMemo } from "solid-js";
import { ExpandablePayload, DirectionIndicator, RoutingDecisionIndicator, CopyButton } from "./EventHelpers";

function exportJSON(data: any, filename: string) {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function RealtimeEventsTable(props: {
  events: any[];
  onClear: () => void;
}) {
  const [sortDir, setSortDir] = createSignal<"desc" | "asc">("desc");

  const sorted = createMemo(() => {
    const evts = [...props.events];
    evts.sort((a: any, b: any) =>
      sortDir() === "desc" ? b.timestamp - a.timestamp : a.timestamp - b.timestamp,
    );
    return evts;
  });

  return (
    <div>
      <div class="flex items-center justify-between mb-3">
        <div class="flex items-center gap-3">
          <h3 class="text-sm font-semibold text-gray-400 uppercase tracking-wide">Live Events ({props.events.length})</h3>
          <button
            onClick={() => setSortDir((d) => d === "desc" ? "asc" : "desc")}
            class="text-[10px] px-2 py-0.5 bg-gray-800 text-gray-400 rounded hover:text-gray-200 cursor-pointer"
          >
            {sortDir() === "desc" ? "v Newest" : "^ Oldest"}
          </button>
        </div>
        <div class="flex items-center gap-2">
          <button onClick={() => exportJSON(sorted(), `realtime-events-${Date.now()}.json`)} class="text-xs text-gray-500 hover:text-gray-300 cursor-pointer">Export</button>
          <button onClick={props.onClear} class="text-xs text-gray-500 hover:text-gray-300 cursor-pointer">Clear</button>
        </div>
      </div>
      <div class="border border-gray-800 rounded-lg overflow-x-auto max-h-96">
        <table class="w-full text-xs font-mono min-w-[1100px]">
          <thead class="bg-gray-900 text-gray-400 sticky top-0">
            <tr>
              <th class="text-left px-3 py-2 font-medium border-b border-gray-800">Time</th>
              <th class="text-left px-3 py-2 font-medium border-b border-gray-800">Type</th>
              <th class="text-left px-3 py-2 font-medium border-b border-gray-800">Project</th>
              <th class="text-left px-3 py-2 font-medium border-b border-gray-800">Sender</th>
              <th class="text-left px-3 py-2 font-medium border-b border-gray-800">User ID</th>
              <th class="text-left px-3 py-2 font-medium border-b border-gray-800 w-16">Dir</th>
              <th class="text-left px-3 py-2 font-medium border-b border-gray-800">Routing</th>
              <th class="text-left px-3 py-2 font-medium border-b border-gray-800">Payload</th>
              <th class="px-1 py-2 w-10 border-b border-gray-800"></th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-800/50">
            <For each={sorted()}>
              {(evt) => (
                <tr class="hover:bg-gray-900/30">
                  <td class="px-3 py-1.5 text-gray-500 whitespace-nowrap">{new Date(evt.timestamp).toLocaleTimeString()}</td>
                  <td class="px-3 py-1.5 text-cyan-400 whitespace-nowrap">{evt.original?.type ?? "?"}</td>
                  <td class="px-3 py-1.5 text-gray-500 whitespace-nowrap">{evt.projectId?.slice(0, 10) ?? "-"}</td>
                  <td class="px-3 py-1.5 whitespace-nowrap">
                    <span class={`${evt.sender?.role === "searcher" ? "text-cyan-400" : evt.sender?.role === "manager" || evt.sender?.role === "owner" ? "text-purple-400" : "text-gray-400"}`}>
                      {evt.sender?.role ?? "?"}
                    </span>
                    <span class="text-gray-600 ml-1">{evt.sender?.connectionId?.slice(0, 8) ?? ""}</span>
                  </td>
                  <td class="px-3 py-1.5 text-gray-500 whitespace-nowrap">{evt.sender?.userId?.slice(0, 10) ?? "-"}</td>
                  <td class="px-3 py-1.5 whitespace-nowrap">
                    <DirectionIndicator channels={evt.channels} />
                  </td>
                  <td class="px-3 py-1.5 whitespace-nowrap">
                    <RoutingDecisionIndicator routing={evt.routing} channels={evt.channels} />
                  </td>
                  <td class="px-3 py-1.5 min-w-[200px]">
                    <ExpandablePayload payload={evt.original} />
                  </td>
                  <td class="px-1 py-1.5">
                    <CopyButton text={JSON.stringify(evt.original, null, 2)} />
                  </td>
                </tr>
              )}
            </For>
          </tbody>
        </table>
      </div>
    </div>
  );
}
